import React from 'react';
import { fitText } from '@/lib/bracket/text';
import { COLORS, HEADER_TONES, TROPHY_PATHS, lineStyle } from './theme';

const fx = (n) => n.toFixed(2);

export const LineSvg = ({ item, M }) => {
  const st = lineStyle(item, M.fs);
  const d = item.pts.map(([x, y], i) => `${i ? 'L' : 'M'}${fx(x)},${fx(y)}`).join(' ');
  return <path d={d} fill="none" stroke={st.stroke} strokeWidth={st.width} strokeDasharray={st.dash} strokeLinejoin="round" strokeLinecap="round" />;
};

export const HeaderSvg = ({ item, M }) => {
  const [bg, fg] = HEADER_TONES[item.tone] || HEADER_TONES.normal;
  const t = fitText(item.text, item.w - M.hfs, M.hfs, 700, 0.75);
  return (
    <g x-excluded="true" data-testid={item.testId}>
      <rect x={item.x} y={item.y} width={item.w} height={item.h} rx={item.h / 2} fill={bg} />
      <text x-excluded="true" x={item.x + item.w / 2} y={item.y + item.h / 2 + t.size * 0.36} textAnchor="middle" fontSize={t.size} fontWeight={700} fill={fg}>{t.text}</text>
    </g>
  );
};

export const NoteSvg = ({ item }) => (
  <text x-excluded="true" x={item.x} y={item.y} textAnchor={item.anchor} fontSize={item.size} fontWeight={item.bold ? 700 : 600}
    fill={item.muted ? COLORS.muted : COLORS.slate}>{item.text}</text>
);

export const ChampionSvg = ({ item, M, noIds, blank, writeIn, champNames, onSlot }) => {
  const silver = item.tone === 'silver';
  const fill = silver ? '#334155' : COLORS.navy;
  const accent = silver ? '#CBD5E1' : '#F5C542';
  const { x, y, w, h } = item;
  const icon = Math.min(h * 0.55, M.fs * 2.2);
  const tx = M.fs * 0.6 + icon + M.fs * 0.5;
  const title = fitText(item.title, w - tx - M.fs * 0.5, M.fs * 0.62, 700);
  const typed = writeIn ? (champNames?.[`champ:${item.title}`] || '') : null;
  const name = fitText(blank ? '' : (typed || item.name || (writeIn ? '' : 'TBD')), w - tx - M.fs * 0.5, M.fs * 1.05, 700, 0.75);
  const slug = item.title.toLowerCase().replace(/\s/g, '-');
  const key = `champ:${item.title}`;
  return (
    <g x-excluded="true" transform={`translate(${fx(x)} ${fx(y)})`} data-testid={noIds ? undefined : `bracket-${slug}`}>
      <rect width={w} height={h} rx={Math.min(M.fs * 0.5, 5)} fill={fill} stroke={accent} strokeWidth={1.2} />
      <g x-excluded="true" transform={`translate(${fx(M.fs * 0.6)} ${fx((h - icon) / 2)}) scale(${(icon / 24).toFixed(4)})`}
        fill="none" stroke={accent} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {TROPHY_PATHS.map((d) => <path key={d} d={d} />)}
      </g>
      <text x-excluded="true" x={tx} y={h / 2 - M.fs * 0.25} fontSize={title.size} fontWeight={700} fill={accent}>{title.text}</text>
      <text x-excluded="true" x={tx} y={h / 2 + M.fs * 0.95} fontSize={name.size} fontWeight={700} fill="#FFFFFF" fillOpacity={item.name || typed ? 1 : 0.6}
        data-testid={noIds ? undefined : `bracket-${slug}-name`}>{name.text}</text>
      {onSlot && (
        <rect className="bk-slot" data-bk-slot={key} data-testid={`bracket-slot-${key}`} width={w} height={h} fill="transparent"
          onClick={(e) => { e.stopPropagation(); onSlot(key, e.currentTarget); }} />
      )}
    </g>
  );
};

// Faint diagonal "preview" mark across free customized previews (screen only).
export const WatermarkSvg = ({ page, text = 'PREVIEW · UPGRADE TO PRO TO PRINT' }) => {
  const rows = [];
  for (let yy = 40; yy < page.h + page.w; yy += 120) rows.push(yy);
  return (
    <g x-excluded="true" pointerEvents="none" data-testid="bracket-watermark" transform={`rotate(-24 ${fx(page.w / 2)} ${fx(page.h / 2)})`}>
      {rows.map((yy) => (
        <text x-excluded="true" key={yy} x={page.w / 2} y={yy - page.w / 3} textAnchor="middle" fontSize={22} fontWeight={700}
          fill={COLORS.navy} fillOpacity={0.07} letterSpacing={3}>{`${text} · ${text}`}</text>
      ))}
    </g>
  );
};

export const TitleBand = ({ layout, title, subtitle, eventLogo, pageLabel, noIds }) => {
  const { page, titleH } = layout;
  const m = page.margin;
  const big = titleH > 40;
  const tfs = big ? 22 : 17;
  const sfs = big ? 9.5 : 8;
  const logoS = titleH - 9;
  const tx = m + (eventLogo ? logoS + 8 : 0);
  const maxW = page.w - m - tx - 90;
  const t = fitText(title || '', maxW, tfs, 700, 0.7);
  const s = fitText(subtitle || '', maxW, sfs, 400, 0.85);
  const base = m + tfs * 0.85;
  return (
    <g x-excluded="true">
      {eventLogo && <image href={eventLogo} x={m} y={m} width={logoS} height={logoS} preserveAspectRatio="xMidYMid meet" />}
      <text x-excluded="true" x={tx} y={base} fontSize={t.size} fontWeight={700} fill={COLORS.navy} data-testid={noIds ? undefined : 'bracket-tournament-name'}>{t.text}</text>
      <text x-excluded="true" x={tx} y={base + sfs * 1.5} fontSize={s.size} fontWeight={400} fill="#64748B">{s.text}</text>
      {pageLabel && <text x-excluded="true" x={page.w - m} y={base} textAnchor="end" fontSize={sfs} fontWeight={700} fill="#64748B">{pageLabel}</text>}
      <rect x={m} y={m + titleH - 5} width={page.w - 2 * m} height={0.6} fill="#CBD5E1" />
      <rect x={m} y={m + titleH - 5.8} width={42} height={2.2} fill={COLORS.red} />
    </g>
  );
};

export const FooterBand = ({ layout, hasDashed }) => {
  const { page } = layout;
  const m = page.margin;
  const y = page.h - m - 2;
  const keys = [['Decided', COLORS.navy, 1.2], ['Upcoming', '#AEB9CE', 0.8]];
  if (hasDashed) keys.push(['Loser path', '#64748B', 0.8, '2.5 2']);
  return (
    <g x-excluded="true" fontSize={6} fontWeight={600} fill="#64748B">
      {keys.map(([label, color, sw, dash], i) => (
        <g x-excluded="true" key={label} transform={`translate(${m + i * 62} ${y})`}>
          <line x1={0} x2={14} y1={-2} y2={-2} stroke={color} strokeWidth={sw} strokeDasharray={dash} />
          <text x-excluded="true" x={18} y={0}>{label}</text>
        </g>
      ))}
      <text x-excluded="true" x={page.w - m} y={y} textAnchor="end" fontWeight={700} fill={COLORS.navy}>scheduleguru.ws</text>
    </g>
  );
};
